import React from 'react';
import Section from './Section';
import { CONTACT_EMAIL, PHONE_NUMBER } from '../constants';

interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'How can your labels prevent outside alcohol at my golf course or sporting venue?',
    answer: 'When applied to your venue’s beverages, our purchased on premises labels help staff quickly identify beverages that were purchased on premises. Having a highly visible temper-evident label applied to each approved beer, cider, or cooler before it leaves the beverage cart or service area makes outside alcohol easier to spot, and helps staff enforce your venue’s rules consistently.',
  },
  {
    question: 'Can the labels be reused?',
    answer: 'Our labels are not reusable. If a label is tampered with, the printed layer and the laminate will separate, making the tampering easy to spot and preventing the label from being transferred to another beverage.',
  },
  {
    question: 'Are the labels waterproof?',
    answer: 'Yes. Unlike the labels from other providers that will simply fall off when wet, our labels are laminated to withstand full immersion. Throw your beverages in an ice bucket or cooler and enjoy them without worrying about the label falling apart.',
  },
  {
    question: 'What are your most popular color options?',
    answer: 'Pink and orange are our most popular options because they offer excellent visibility and strong contrast against the most commonly sold beverages. Blue and green are also highly visible and remain excellent choices, especially when they align with your organization’s branding.',
  },
  {
    question: 'Can I order labels with a custom design or color?',
    answer: 'Yes. We can work with you on custom artwork, colors, and branding. Custom designs will affect the price when compared to our generic labels, so please contact us for a personalized quote. Please note that custom orders typically take between 3-4 weeks to produce, and minimum order quantities will apply.',
  },
  {
    question: 'Where are the labels made?',
    answer: 'Our labels are proudly Canadian made, and are produced in Vancouver, British Columbia.',
  },
  {
    question: 'Can I order labels for me and my friends to use at our favorite golf course?',
    answer: 'No. Our labels are available exclusively to golf courses, event planners, and other sporting venues for their own beverage-service programs. We may request business or venue verification to confirm that an order is for an eligible use.',
  },
  {
    question: 'What beverages can the labels be used on?',
    answer: 'They are designed for adult beverages such as beer, cider, and coolers in cans or bottles. Their strong adhesive performs well on a variety of beverage-container surfaces, including cold containers.',
  },
  {
    question: 'How many labels are included in an order?',
    answer: 'Each roll contains 1,000 labels, and the minimum order is one roll. That makes it easy to start with a small quantity or stock up for a busy season or event schedule.',
  },
  {
    question: 'How quickly will my labels arrive?',
    answer: 'Typical transit time is 2–4 business days in Canada and 3–5 business days in the United States. Contact us if you have an event deadline and we will help you plan your order.',
  },
{
    question: 'How do I place an order?',
    answer: (
      <>
        Simply reach out to us by <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-emerald-600 hover:text-emerald-700 hover:underline">email</a>, call us at <a href={`tel:${PHONE_NUMBER}`} className="font-medium text-emerald-600 hover:text-emerald-700 hover:underline">{PHONE_NUMBER}</a>, or use our <a href="#contact" className="font-medium text-emerald-600 hover:text-emerald-700 hover:underline">contact form</a>. We will be happy to answer your questions, discuss your requirements, and help you get started.
      </>
    ),
  },
];

const FAQSection: React.FC = () => {
  return (
    <Section id="faq" title="Frequently Asked Questions" className="bg-gray-100">
      <div className="max-w-4xl mx-auto space-y-4">
        {FAQ_ITEMS.map((item) => (
          <details
            key={item.question}
            className="group bg-white rounded-xl border border-gray-200 shadow-sm open:border-emerald-200 open:shadow-md transition-all"
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-6 px-6 py-5 text-left text-lg font-semibold text-gray-800 marker:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-inset">
              <span>{item.question}</span>
              <span className="flex-shrink-0 text-2xl font-normal leading-none text-emerald-600 transition-transform group-open:rotate-45" aria-hidden="true">
                +
              </span>
            </summary>
            <div className="px-6 pb-6 text-base leading-relaxed text-gray-600">
              {item.answer}
            </div>
          </details>
        ))}
      </div>
    </Section>
  );
};

export default FAQSection;
